import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTestQuestions, getQuestionAnswers, Answer } from '../services/api';
import axios from 'axios';
import './TestPage.css';
import { Question } from '../types/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

interface Answers {
  [questionId: number]: string;
}

interface Option {
  id: string;
  text: string;
  serial?: number;
  score?: number;
}

interface SessionResponse {
  sessionId: number;
  userId: number;
  testId: number;
  status: string;
  startTime: string;
  endTime: string | null;
  currentQuestionId: number | null;
  timeLimit: number | null;
  forMyself: boolean;
  timed: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  profileId: number | null;
}

// Add SidedGraph4Drag component
const SidedGraph4DragComponent: React.FC<{
  content: any;
  questionId: number;
  onAnswer: (questionId: number, answer: string) => void;
}> = ({ content, questionId, onAnswer }) => {
  const [draggablePosition, setDraggablePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pointRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current || !pointRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const point = pointRef.current.getBoundingClientRect();
    
    // Calculate new position relative to container center
    const centerX = container.width / 2;
    const centerY = container.height / 2;
    
    // Calculate distance from center
    const maxDistance = Math.min(centerX, centerY) - point.width / 2;
    const mouseX = e.clientX - container.left - centerX;
    const mouseY = e.clientY - container.top - centerY;
    
    // Calculate angle and distance
    const angle = Math.atan2(mouseY, mouseX);
    const distance = Math.min(
      Math.sqrt(mouseX * mouseX + mouseY * mouseY),
      maxDistance
    );
    
    // Calculate new position
    const newX = Math.cos(angle) * distance;
    const newY = Math.sin(angle) * distance;

    setDraggablePosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Calculate the answer based on position
    if (containerRef.current && pointRef.current) {
      const container = containerRef.current.getBoundingClientRect();
      const point = pointRef.current.getBoundingClientRect();
      
      // Calculate relative position (-1 to 1)
      const relativeX = draggablePosition.x / (container.width / 2 - point.width / 2);
      const relativeY = draggablePosition.y / (container.height / 2 - point.height / 2);
      
      // Convert to answer format (e.g., "x,y")
      const answer = `${relativeX.toFixed(2)},${relativeY.toFixed(2)}`;
      onAnswer(questionId, answer);
    }
  };

  // Add touch event handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !containerRef.current || !pointRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const point = pointRef.current.getBoundingClientRect();
    
    // Calculate new position relative to container center
    const centerX = container.width / 2;
    const centerY = container.height / 2;
    
    // Get touch position
    const touch = e.touches[0];
    const mouseX = touch.clientX - container.left - centerX;
    const mouseY = touch.clientY - container.top - centerY;
    
    // Calculate distance from center
    const maxDistance = Math.min(centerX, centerY) - point.width / 2;
    
    // Calculate angle and distance
    const angle = Math.atan2(mouseY, mouseX);
    const distance = Math.min(
      Math.sqrt(mouseX * mouseX + mouseY * mouseY),
      maxDistance
    );
    
    // Calculate new position
    const newX = Math.cos(angle) * distance;
    const newY = Math.sin(angle) * distance;

    setDraggablePosition({ x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Calculate the answer based on position
    if (containerRef.current && pointRef.current) {
      const container = containerRef.current.getBoundingClientRect();
      const point = pointRef.current.getBoundingClientRect();
      
      // Calculate relative position (-1 to 1)
      const relativeX = draggablePosition.x / (container.width / 2 - point.width / 2);
      const relativeY = draggablePosition.y / (container.height / 2 - point.height / 2);
      
      // Convert to answer format (e.g., "x,y")
      const answer = `${relativeX.toFixed(2)},${relativeY.toFixed(2)}`;
      onAnswer(questionId, answer);
    }
  };

  return (
    <div className="sided-graph-container">
      <h2 className="question-text">{content.title || t('dragPointInstructions')}</h2>
      <div 
        className="sided-graph"
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Text Labels */}
        <span className="graph-label top">{content.topName || t('top')}</span>
        <span className="graph-label bottom">{content.bottomName || t('bottom')}</span>
        <span className="graph-label left">{content.leftName || t('left')}</span>
        <span className="graph-label right">{content.rightName || t('right')}</span>

        {/* Draggable Point */}
        <div 
          className="draggable-point"
          ref={pointRef}
          style={{
            position: 'absolute',
            left: `calc(50% + ${draggablePosition.x}px)`, 
            top: `calc(50% + ${draggablePosition.y}px)`,
            transform: 'translate(-50%, -50%)',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
      
        ></div>
      </div>
    </div>
  );
};

const AgreeDisagreeComponent: React.FC<{
  content: any;
  questionId: number;
  onAnswer: (questionId: number, answer: string) => void;
}> = ({ content, questionId, onAnswer }) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    onAnswer(questionId, option);
  };

  return (
    <div className="agree-disagree-container">
      <div className="agree-disagree-statement">
        {content.textContent}
      </div>
      <div className="agree-disagree-options">
        <button
          className={`agree-disagree-button ${selectedOption === 'agree' ? 'selected' : ''}`}
          onClick={() => handleOptionClick('agree')}
        >
          {t('agree')}
        </button>
        <button
          className={`agree-disagree-button ${selectedOption === 'disagree' ? 'selected' : ''}`}
          onClick={() => handleOptionClick('disagree')}
        >
          {t('disagree')}
        </button>
      </div>
    </div>
  );
};

const LuscherColorTestComponent: React.FC<{
  content: any;
  questionId: number;
  onAnswer: (questionId: number, answer: string) => void;
}> = ({ content, questionId, onAnswer }) => {
  const [selectedColors, setSelectedColors] = useState<number[]>([]);
  const [draggedColor, setDraggedColor] = useState<number | null>(null);
  const { t } = useTranslation();

  const handleColorClick = (colorIndex: number) => {
    let newSelectedColors: number[];
    if (selectedColors.includes(colorIndex)) {
      newSelectedColors = selectedColors.filter(c => c !== colorIndex);
    } else {
      newSelectedColors = [...selectedColors, colorIndex];
    }
    setSelectedColors(newSelectedColors);
    
    // If all colors are selected, send the answer
    if (newSelectedColors.length === 8) {
      const answer = newSelectedColors.join(',');
      onAnswer(questionId, answer);
    }
  };

  const handleDragStart = (e: React.DragEvent, colorIndex: number) => {
    setDraggedColor(colorIndex);
    e.dataTransfer.setData('text/plain', colorIndex.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedColor === null) return;

    const newSelectedColors = [...selectedColors];
    const draggedIndex = newSelectedColors.indexOf(draggedColor);
    const targetPosition = newSelectedColors.indexOf(targetIndex);

    if (draggedIndex !== -1 && targetPosition !== -1) {
      newSelectedColors.splice(draggedIndex, 1);
      newSelectedColors.splice(targetPosition, 0, draggedColor);
      setSelectedColors(newSelectedColors);
      
      // If all colors are selected, send the answer
      if (newSelectedColors.length === 8) {
        const answer = newSelectedColors.join(',');
        onAnswer(questionId, answer);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedColor(null);
  };

  return (
    <div className="luscher-container">
      <div className="luscher-description">
        {JSON.parse(content.textContent)[0].description}
      </div>
      <div className="luscher-colors">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((colorIndex) => (
          <div
            key={colorIndex}
            className={`luscher-color luscher-color-${colorIndex} ${
              selectedColors.includes(colorIndex) ? 'selected' : ''
            }`}
            draggable
            onDragStart={(e) => handleDragStart(e, colorIndex)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, colorIndex)}
            onDragEnd={handleDragEnd}
            onClick={() => handleColorClick(colorIndex)}
          >
            <span className="luscher-color-number">{colorIndex + 1}</span>
          </div>
        ))}
      </div>
      {selectedColors.length === 8 && (
        <div style={{ color: 'green', marginTop: '1rem' }}>
          {t('allColorsSelected')}
        </div>
      )}
    </div>
  );
};

const TwoCardsSideBySideComponent: React.FC<{
  content: any;
  questionId: number;
  onAnswer: (questionId: number, answer: string) => void;
}> = ({ content, questionId, onAnswer }) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleCardClick = (option: string) => {
    setSelectedOption(option);
    onAnswer(questionId, option);
  };

  const parsedContent = JSON.parse(content.textContent)[0];

  return (
    <div className="two-cards-container">
      <div className="two-cards-description">
        {parsedContent.description}
      </div>
      <div className="two-cards-options">
        <div
          className={`two-cards-card ${selectedOption === 'left' ? 'selected' : ''}`}
          onClick={() => handleCardClick('left')}
        >
          {parsedContent.left}
        </div>
        <div
          className={`two-cards-card ${selectedOption === 'right' ? 'selected' : ''}`}
          onClick={() => handleCardClick('right')}
        >
          {parsedContent.right}
        </div>
      </div>
    </div>
  );
};

const TestPage: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState<boolean>(false);
  const { language } = useLanguage();
  const preferredLanguage = language.toLowerCase();
  const { t } = useTranslation();
  const [questionOptions, setQuestionOptions] = useState<{[questionId: number]: Option[]}>({});
  const [questionMechanics, setQuestionMechanics] = useState<{[questionId: number]: string}>({});
  const [sessionId, setSessionId] = useState<number | null>(null);
  const sessionAttemptedRef = useRef<boolean>(false);
  const [completingSession, setCompletingSession] = useState<boolean>(false);
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, createRandomUser } = useAuth();
  const [showAnswerRequired, setShowAnswerRequired] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [loadingResults, setLoadingResults] = useState<boolean>(false);

  useEffect(() => {
    if (!currentUser && !sessionAttemptedRef.current) {
      setError('No authenticated user');
      setLoading(false);
      return;
    }
  }, [currentUser]);

  // Function to get authentication header
  const getAuthHeader = () => {
    const userDataStr = localStorage.getItem('whomii_user');
    let basicAuth = '';
    
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      
      // Log user credentials for debugging - REMOVE THIS IN PRODUCTION
      console.log('DEBUG - User email:', userData.email);
      console.log('DEBUG - User password:', userData.password);
      
      basicAuth = `Basic ${btoa(`${userData.email}:${userData.password}`)}`;
      
      // Log the complete auth header
      console.log('DEBUG - Auth header:', basicAuth);
    }
    
    return basicAuth;
  };

  // We'll use a different approach to avoid the TypeScript errors
  useEffect(() => {
    const createTestSession = async () => {
      // Prevent duplicate requests in StrictMode
      if (sessionAttemptedRef.current) {
        return;
      }
      sessionAttemptedRef.current = true;
      
      try {
        if (!testId) {
          setError('Test ID is missing');
          setLoading(false);
          return;
        }

        const basicAuth = getAuthHeader();
        let sessionEstablished = false;
        
        // Try to create a new test session
        try {
          console.log('Attempting to create test session...');
          const response = await fetch('https://apiwhomii-z2uc5.ondigitalocean.app/api/v1/test-sessions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': basicAuth,
            },
            body: JSON.stringify({
              testId: parseInt(testId),
              forMyself: true,
              timeLimit: 0,
              timed: true
            })
          });

          // Parse response if possible
          let responseData = null;
          try {
            responseData = await response.json();
            console.log('Session response:', response.status, responseData);
          } catch (e) {
            console.error('Error parsing response:', e);
          }

          // Check if we got a successful response
          if (response.ok && responseData && responseData) {
            // Use type assertion to avoid TypeScript errors
            const sessionData = responseData as any;
            if (sessionData.sessionId) {
              console.log('Session created successfully:', sessionData);
              setSessionId(sessionData.sessionId);
              sessionEstablished = true;
            }
          } else if (response.status === 406) {
            console.warn('Got 406 error - will try to fetch existing sessions');
            
            // If we got a 406, try to fetch existing sessions
            const sessionsResponse = await fetch('https://apiwhomii-z2uc5.ondigitalocean.app/api/v1/test-sessions', {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': basicAuth,
              }
            });
            
            if (sessionsResponse.ok) {
              const sessionsData = await sessionsResponse.json();
              console.log('Retrieved user sessions:', sessionsData);
              
              // Check if we have any existing sessions for this test
              if (Array.isArray(sessionsData)) {
                // Use any/unknown to bypass TypeScript errors
                const matchingSession = sessionsData.find((s: any) => 
                  s.testId === parseInt(testId) && s.status === 'IN_PROGRESS'
                );
                
                if (matchingSession) {
                  console.log('Found existing session:', matchingSession);
                  setSessionId((matchingSession as any).sessionId);
                  sessionEstablished = true;
                }
              }
            }
          }
        } catch (err) {
          console.error('Error handling sessions:', err);
        }
        
        // After session attempt, fetch the questions
        try {
          console.log('Fetching test questions...');
          const questionsData = await getTestQuestions(testId);
          console.log("Questions data:", questionsData);
          
          if (questionsData && questionsData.length > 0) {
            setQuestions(questionsData);
            
            // Fetch answers for each question
            const optionsPromises = questionsData.map(question => 
              fetchQuestionOptions(question.questionId)
            );
            
            await Promise.all(optionsPromises);
          } else {
            setError('No questions found for this test.');
          }
        } catch (err) {
          console.error('Error loading questions:', err);
          setError('Failed to load questions. Please try again later.');
        }
      } catch (err) {
        setError('Failed to load test. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    createTestSession();
  }, [testId]);
  
  const fetchQuestionOptions = async (questionId: number) => {
    try {
      const answerData = await getQuestionAnswers(questionId);
      console.log(`Answers for question ${questionId}:`, answerData);
      
      // Determine the mechanic type from the first answer
      if (answerData.length > 0 && answerData[0].answerContentList.length > 0) {
        const mechanic = answerData[0].answerContentList[0].mechanic;
        // Store the mechanic type for this question
        setQuestionMechanics(prev => ({
          ...prev,
          [questionId]: Object.values(mechanic)[0] || ''
        }));
      }
      
      // Create options from the answer data
      const options = answerData.map(answer => {
        // Find content in the preferred language or fallback to first available
        const content = answer.answerContentList.find(c => 
          c.languageCode.toLowerCase() === preferredLanguage
        ) || answer.answerContentList[0];
        
        return {
          id: answer.answerId.toString(),
          text: content.textContent || '',
          serial: answer.serial,
          score: answer.numericScore
        };
      });
      
      // Sort options by serial
      options.sort((a, b) => a.serial - b.serial);
      
      setQuestionOptions(prev => ({
        ...prev,
        [questionId]: options
      }));
      
    } catch (err) {
      console.error(`Failed to load options for question ${questionId}:`, err);
    }
  };

  // When preferred language changes, update options for the current question
  useEffect(() => {
    if (questions.length > 0) {
      const currentQuestion = questions[currentQuestionIndex];
      fetchQuestionOptions(currentQuestion.questionId);
    }
  }, [preferredLanguage, currentQuestionIndex, questions]);

  // Simple answer selection without API call
  const handleAnswer = (questionId: number, answerId: string) => {
    // Only update local state
    setAnswers({
      ...answers,
      [questionId]: answerId
    });
  };

  // Send answer to API when next is clicked
  const sendAnswerToApi = async (questionId: number, answerId: string) => {
    if (sessionId) {
      try {
        const basicAuth = getAuthHeader();
        
        // Ensure answerId is a valid number
        let answerIdNum: number | undefined = undefined;
        try {
          // Don't use parseInt which can partially convert strings
          // Instead, check if the string consists only of digits
          if (/^\d+$/.test(answerId)) {
            answerIdNum = parseInt(answerId);
          }
        } catch (e) {
          console.warn('Could not convert answerId to number:', answerId);
        }
        
        // Get question data
        const question = questions.find(q => q.questionId === questionId);
        if (!question) {
          console.error('Question not found:', questionId);
          return;
        }
        
        // Create FormData object for the request
        const formData = new FormData();
        
        // Add required fields
        formData.append('testSessionId', sessionId.toString());
        formData.append('questionId', questionId.toString());
        formData.append('answerProvided', 'true');
        
        // Add answer field
        if (answerIdNum !== undefined) {
          formData.append('answerId', answerIdNum.toString());
        } else {
          // Ensure textResponse is at least 3 characters long
          let textResp = answerId.toString();
          if (textResp.length < 3) {
            textResp = textResp.padEnd(3, '_'); // Pad with underscore if needed
          }
          formData.append('textResponse', textResp);
        }
        
        console.log('Sending answer to API as FormData');
        
        const response = await fetch('https://apiwhomii-z2uc5.ondigitalocean.app/api/v1/test-session-records', {
          method: 'POST',
          headers: {
            'Authorization': basicAuth,
            // Note: Do not set Content-Type header for FormData
            // The browser will automatically set it with the correct boundary
          },
          body: formData
        });
        
        if (response.ok) {
          console.log('Answer recorded successfully');
        } else {
          console.error('Failed to record answer:', response.status);
          try {
            const errorData = await response.json();
            console.error('Error details:', errorData);
            
            // Log more detailed debugging info - convert FormData to an object for logging
            const formDataEntries: Record<string, any> = {};
            formData.forEach((value, key) => {
              formDataEntries[key] = value;
            });
            console.log('Full payload sent (FormData):', formDataEntries);
            console.log('Current question:', question);
            console.log('Current answer:', answerId);
          } catch (e) {
            // Ignore JSON parsing errors
          }
        }
      } catch (err) {
        console.error('Error recording answer:', err);
      }
    } else {
      console.warn('Cannot record answer: No active session ID');
    }
  };

  const handleNext = async () => {
    // Only proceed if the current question has an answer
    const currentQuestion = questions[currentQuestionIndex];
    if (!answers[currentQuestion.questionId]) {
      // Show the message instead of alert
      setShowAnswerRequired(true);
      return;
    }

    // Reset the message when proceeding with a valid answer
    setShowAnswerRequired(false);

    // Send the current answer to the API
    await sendAnswerToApi(
      currentQuestion.questionId, 
      answers[currentQuestion.questionId]
    );

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // All questions are now answered, mark session as completed
      await completeTestSession();
      setCompleted(true);
    }
  };

  // Function to mark the test session as completed - simplified approach
  const completeTestSession = async () => {
    if (sessionId) {
      try {
        setCompletingSession(true);
        
        const userDataStr = localStorage.getItem('whomii_user');
        if (!userDataStr) {
          console.error('No user data found in localStorage');
          setCompletingSession(false);
          return;
        }
        
        const userData = JSON.parse(userDataStr);
        console.log('User data:', { 
          email: userData.email, 
          hasPassword: !!userData.password,
          token: userData.token ? userData.token.substring(0, 10) + '...' : 'none'
        });
        
        // Set up request with proper credentials
        const url = `https://apiwhomii-z2uc5.ondigitalocean.app/api/v1/test-sessions/${sessionId}/change-status?status=COMPLETED`;
        console.log('Making API call to:', url);
        
        try {
          // Add delay before sending request (like in Flutter implementation)
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const basicAuth = `Basic ${btoa(`${userData.email}:${userData.password}`)}`;
          
          // Use axios.patch instead of XMLHttpRequest
          console.log('Attempting axios.patch call to:', url);
          const response = await axios.post(url, null, {
            headers: {
              'Authorization': basicAuth,
              // Don't set Content-Type to avoid preflight CORS issues
            },
            timeout: 30000 // 30 second timeout
          });
          
          console.log('Response status:', response.status);
          console.log('Response data:', response.data);
          
          if (response.status >= 200 && response.status < 300) {
            console.log('Test session completed successfully');
            
            // Check if we have profileId in the response
            if (response.data && response.data.profileId) {
              console.log('Profile ID from response:', response.data.profileId);
            }
            
            // Fetch test results after successful completion
            await fetchTestResult(sessionId);
            
          } else {
            console.error(`Failed with status: ${response.status}`);
          }
          setCompletingSession(false);
        } catch (err) {
          console.error('API call error:', err);
          
          // Handle axios errors
          if (axios.isAxiosError(err)) {
            const status = err.response?.status || 'unknown';
            const data = err.response?.data || 'No data';
            console.error(`Axios error: ${status}`, data);
          } else {
            // Generic error handling
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            console.error(`Error completing test session: ${errorMessage}`);
          }
          setCompletingSession(false);
        }
      } catch (err) {
        console.error('Error in test session completion process:', err);
        setCompletingSession(false);
      }
    } else {
      console.warn('Cannot complete test session: No active session ID');
    }
  };

  // Add fetchTestResult function
  const fetchTestResult = async (sessionId: number) => {
    try {
      setLoadingResults(true);
      
      // Get user data from localStorage
      const userDataStr = localStorage.getItem('whomii_user');
      if (!userDataStr) {
        throw new Error('User not authenticated');
      }
      
      const userData = JSON.parse(userDataStr);
      const basicAuth = `Basic ${btoa(`${userData.email}:${userData.password}`)}`;
      
      const response = await fetch(`https://apiwhomii-z2uc5.ondigitalocean.app/api/v1/test-sessions/${sessionId}/result`, {
        headers: {
          'Authorization': basicAuth,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Test result data:", data);
      setTestResult(data);
    } catch (error) {
      console.error('Error fetching test result:', error);
    } finally {
      setLoadingResults(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleFinish = async () => {
    // No need to call completeTestSession again since it's already called in handleNext
    // Just navigate back to homepage
    navigate('/');
  };

  // Check if all questions have been answered
  const allQuestionsAnswered = (): boolean => {
    if (questions.length === 0) return false;
    
    return questions.every(question => !!answers[question.questionId]);
  };

  const handleGuestLogin = async () => {
    try {
      await createRandomUser();
      navigate('/');
    } catch (error) {
      console.error('Guest login failed:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="error-container">
        <div className="error-message">{t('pleaseLogin')}</div>
        <div className="auth-options">
          <button 
            className="auth-button login-button"
            onClick={() => navigate('/test')}
          >
            {t('login')}
          </button>
          <button 
            className="auth-button register-button"
            onClick={() => navigate('/register')}
          >
            {t('register')}
          </button>
          <button 
            className="auth-button guest-button"
            onClick={handleGuestLogin}
          >
            {t('continueAsGuest')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">{t('loadingQuestions')}</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (questions.length === 0) {
    return <div className="error">{t('noQuestions')}</div>;
  }

  if (completed) {
    return (
      <div className="test-completed card">
        <div className="completion-content">
          <div className="completion-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.5028C5.79935 3.86354 7.69279 2.72111 9.79619 2.24587C11.8996 1.77063 14.1003 1.98806 16.07 2.86572" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1>{t('testCompleted')}</h1>
          <p>{t('thankYou')}</p>
          
          {loadingResults && (
            <div className="loading-results">{t('loadingResults')}</div>
          )}

          {!loadingResults && testResult && (
            <div className="test-results">
              <h2>{t('testResults')}</h2>
              <div className="results-container">
                {testResult.profile && (
                  <div className="profile-result">
                    <h3>{testResult.profile.description[preferredLanguage]?.name || testResult.profile.description.en?.name}</h3>
                    <p className="profile-short-desc">
                      {testResult.profile.description[preferredLanguage]?.shortDescription || testResult.profile.description.en?.shortDescription}
                    </p>
                    <p className="profile-long-desc">
                      {testResult.profile.description[preferredLanguage]?.longDescription || testResult.profile.description.en?.longDescription}
                    </p>
                  </div>
                )}
                
                {testResult.profile?.keywords && testResult.profile.keywords.length > 0 && (
                  <div className="keywords-section">
                    <h3>{t('keywords')}</h3>
                    <div className="keywords-container">
                      {testResult.profile.keywords.map((keyword: any, index: number) => (
                        <div key={index} className="keyword-item">
                          <h4>{keyword.description[preferredLanguage]?.name || keyword.description.en?.name}</h4>
                          <p>
                            {keyword.description[preferredLanguage]?.description || keyword.description.en?.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Fallback for old format */}
                {testResult.testResultTranslationList && testResult.testResultTranslationList.length > 0 && (
                  testResult.testResultTranslationList.map((result: any, index: number) => (
                    <div key={index} className="result-item">
                      <h3>{result.title}</h3>
                      <p>{result.description}</p>
                    </div>
                  ))
                )}
                
                {testResult.testSessionTraitResultList && testResult.testSessionTraitResultList.length > 0 && (
                  testResult.testSessionTraitResultList.map((trait: any, index: number) => (
                    <div key={index} className="trait-item">
                      <h3>{trait.traitCode}</h3>
                      <div className="trait-score">
                        {t('score')}: {trait.score.toFixed(2)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          <div className="action-buttons">
            <button
              onClick={handleFinish}
              className="action-button home-button"
            >
              {t('returnHome')}
            </button>
            
            {testResult ? (
              <button 
                onClick={() => navigate('/profile')}
                className="action-button profile-button"
              >
                {t('goToProfile')}
              </button>
            ) : (
              <button 
                onClick={() => completeTestSession()} 
                className={`action-button complete-button ${completingSession ? 'disabled' : ''}`}
                disabled={completingSession}
              >
                <span className="button-icon">✓</span>
                {completingSession ? t('processing') : t('testCompleteApi')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  
  // Get content based on user's language preference (defaulting to English)
  const preferredContent = currentQuestion.questionContentList.find(content => 
    content.languageCode.toLowerCase() === preferredLanguage
  ) || currentQuestion.questionContentList[0]; // Fallback to first content if preferred language not found
  
  let questionDescription = t('noQuestionAvailable');
  
  try {
    if (preferredContent && preferredContent.textContent) {
      const parsedContent = JSON.parse(preferredContent.textContent);
      console.log('Parsed question content:', parsedContent);
      if (parsedContent && parsedContent.length > 0) {
        // Get the question description from the parsed content
        questionDescription = parsedContent[0].description || t('noQuestionAvailable');
      }
    }
  } catch (e) {
    console.error('Error parsing question content:', e);
    // If parsing fails, try to use the direct textContent as the question
    if (preferredContent) {
      // QuestionContent doesn't have a description property directly
      questionDescription = t('noQuestionAvailable');
    }
  }

  const questionType = currentQuestion.questionType;
  const mechanicType = preferredContent?.mechanic?.name;

  // Default options for multiple choice questions
  const defaultOptions: Option[] = [
    { id: 'option1', text: t('yes') },
    { id: 'option2', text: t('no') },
    { id: 'option3', text: t('sometimes') },
    { id: 'option4', text: t('notSure') }
  ];
  
  // Get options for the current question from state or use defaults
  const currentOptions = questionOptions[currentQuestion.questionId] || defaultOptions;
  
  // For special question types like BalloonInflation, we'd render different UI
  const renderQuestionContent = () => {
    if (mechanicType === 'BalloonInflation') {
      return (
        <div className="balloon-game">
          <p>{questionDescription}</p>
          <div className="balloon">
            <button 
              className="pump-button btn btn-primary"
              onClick={() => handleAnswer(currentQuestion.questionId, 'balloon-pump')}
            >
              {t('pumpBalloon')}
            </button>
          </div>
        </div>
      );
    }

    if (mechanicType === 'SidedGraph4Drag') {
      try {
        const parsedContent = JSON.parse(preferredContent.textContent)[0];
        return (
          <SidedGraph4DragComponent
            content={parsedContent}
            questionId={currentQuestion.questionId}
            onAnswer={handleAnswer}
          />
        );
      } catch (e) {
        console.error('Error parsing SidedGraph4Drag content:', e);
        return <div className="error">{t('noQuestionAvailable')}</div>;
      }
    }

    if (mechanicType === 'MultipleChoice') {
      return (
        <>
          <h2 className="question-text">{questionDescription}</h2>
          <div className="answer-options">
            {currentOptions.map((option) => (
              <button 
                key={option.id}
                className={`answer-option ${answers[currentQuestion.questionId] === option.id ? 'selected' : ''}`}
                onClick={() => handleAnswer(currentQuestion.questionId, option.id)}
              >
                {option.text}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Add MultipleShortChoice mechanic type
    if (mechanicType === 'MultipleShortChoice') {
      return (
        <>
          <h2 className="question-text">{questionDescription}</h2>
          <div className="answer-options">
            {currentOptions.map((option) => (
              <button 
                key={option.id}
                className={`answer-option ${answers[currentQuestion.questionId] === option.id ? 'selected' : ''}`}
                onClick={() => handleAnswer(currentQuestion.questionId, option.id)}
              >
                {option.text}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Add IdealFormulaTube mechanic type
    if (mechanicType === 'IdealFormulaTube') {
      return (
        <div className="formula-tube-container">
          <h2 className="question-text">{questionDescription}</h2>
          <div className="formula-input-container">
            <input
              type="text"
              className="formula-input"
              
              value={answers[currentQuestion.questionId] || ''}
              onChange={(e) => handleAnswer(currentQuestion.questionId, e.target.value)}
            />
          </div>
        </div>
      );
    }

    // OppositePoleLikert mechanic - for Likert scale questions
    if (mechanicType === 'OppositePoleLikert' || questionMechanics[currentQuestion.questionId] === 'OppositePoleLikert') {
      // Options should already be sorted by serial from fetchQuestionOptions
      const sortedOptions = currentOptions;
      
      // Determine if we have textual content for the scale points
      const hasTextLabels = sortedOptions.some(option => option.text && option.text.trim() !== '');
      
      return (
        <>
          <h2 className="question-text">{questionDescription}</h2>
          
          <div className="likert-scale-container">
            <div className="likert-scale">
              {sortedOptions.map((option) => (
                <div key={option.id} className="likert-option">
                  <button
                    className={`likert-button ${answers[currentQuestion.questionId] === option.id ? 'selected' : ''}`}
                    onClick={() => handleAnswer(currentQuestion.questionId, option.id)}
                  >
                    {option.score !== undefined ? option.score : '•'}
                  </button>
                  {hasTextLabels && option.text && (
                    <span className="likert-label">{option.text}</span>
                  )}
                </div>
              ))}
            </div>
            
            {/* Scale labels */}
            <div className="likert-scale-labels">
              <div className="likert-left-label">{t('stronglyDisagree')}</div>
              <div className="likert-right-label">{t('stronglyAgree')}</div>
            </div>
          </div>
        </>
      );
    }

    // Default rendering for other question types
    return (
      <>
        <h2 className="question-text">{questionDescription}</h2>
        <div className="answer-options">
          {currentOptions.map((option) => (
            <button 
              key={option.id}
              className={`answer-option ${answers[currentQuestion.questionId] === option.id ? 'selected' : ''}`}
              onClick={() => handleAnswer(currentQuestion.questionId, option.id)}
            >
              {option.text}
            </button>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="test-page">
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
        <div className="progress-indicator">
          {t('questionOf', { current: currentQuestionIndex + 1, total: questions.length })}
        </div>
      </div>

      <div className="question-container">
        {renderQuestionContent()}
        
        {showAnswerRequired && (
          <div className="answer-required-message">
            {t('selectAnswer')}
          </div>
        )}
        
        <div className="navigation-buttons">
          <button 
            className="nav-button prev"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            {t('previousQuestion')}
          </button>
          <button 
            className="nav-button next"
            onClick={handleNext}
          >
            {currentQuestionIndex < questions.length - 1 ? t('nextQuestion') : t('submitAnswers')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestPage;